import { useState, useCallback, type FormEvent } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useAuthStore } from "@/stores/auth-store";
import { STRINGS } from "@/constants/strings";
import { LoaderCircleIcon, ShieldCheckIcon, CopyIcon, CheckIcon } from "lucide-react";

export default function MfaSetupPage() {
  const { confirmMfaSetup, mfaSetupData } = useAuthContext();
  const error = useAuthStore((s) => s.error);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopySecret = useCallback(async () => {
    if (!mfaSetupData?.secretCode) return;
    await navigator.clipboard.writeText(mfaSetupData.secretCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [mfaSetupData?.secretCode]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const code = (
      form.elements.namedItem("mfaSetupCode") as HTMLInputElement
    ).value.trim();

    setIsSubmitting(true);
    try {
      await confirmMfaSetup(code);
    } catch {
      // error set in store
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!mfaSetupData) {
    return null;
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
                <ShieldCheckIcon className="size-6 text-primary" />
              </div>
              <h1 className="text-xl font-bold">
                {STRINGS.auth.mfaSetupTitle}
              </h1>
              <FieldDescription>
                {STRINGS.auth.mfaSetupDescription}
              </FieldDescription>
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex justify-center rounded-lg border bg-white p-4">
              <QRCodeSVG value={mfaSetupData.qrUri} size={200} level="M" />
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-medium">
                {STRINGS.auth.mfaSetupSecretLabel}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded-md bg-muted px-3 py-2 text-xs font-mono">
                  {mfaSetupData.secretCode}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopySecret}
                  className="shrink-0"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="size-3.5" />
                      {STRINGS.auth.mfaSecretCopied}
                    </>
                  ) : (
                    <>
                      <CopyIcon className="size-3.5" />
                      {STRINGS.auth.mfaCopySecret}
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {STRINGS.auth.mfaSetupSecretDescription}
              </p>
            </div>

            <Field>
              <FieldLabel htmlFor="mfaSetupCode">
                {STRINGS.auth.mfaSetupConfirmLabel}
              </FieldLabel>
              <Input
                id="mfaSetupCode"
                name="mfaSetupCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder={STRINGS.auth.mfaSetupConfirmPlaceholder}
                autoComplete="one-time-code"
                autoFocus
                required
                disabled={isSubmitting}
              />
            </Field>

            <Field>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <LoaderCircleIcon className="animate-spin" />
                    {STRINGS.auth.mfaSetupConfirming}
                  </>
                ) : (
                  STRINGS.auth.mfaSetupConfirmButton
                )}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </div>
    </div>
  );
}
